// --------------------------------------------------------------
// Created On: 2025-11-20
// Author: Dimitra Weinstein
//
// Last Modified: 2025-12-02
// Modified By: Dimitra Weinstein
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import useReduxSelector from "src/hooks/useReduxSelector";
import LeadTime from "src/components/LeadTime/LeadTime";
import styles from "./PrintCartItems.module.scss";

// Renders a list of items in the shopping cart for printing.
export default function PrintCartItems(props: Props): Component {
  const currentUser = useReduxSelector((state) => state.currentUser);
  const contentToPrint = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    documentTitle: `${currentUser.userName}'s Shopping Cart`,
  });

  // Returns paint color name for display.
  function getPaintColorName(paintColorId: number | null): string {
    let paintColor = "";
    if (paintColorId === null) {
      paintColor = "N/A";
    } else {
      const paintColorIndex = props.paintColors.findIndex((paintColor) => paintColor.paintColorId === paintColorId);
      if (paintColorIndex !== -1) {
        paintColor = props.paintColors[paintColorIndex].name;
      }
    }
    return paintColor;
  }

  // Returns impeller trim value for display.
  function getImpellerTrim(impellerTrim: string | null): string {
    let trimValue = "";
    if (impellerTrim === null) {
      trimValue = "N/A";
    } else if (impellerTrim === "0.00" || impellerTrim === "0.0") {
      trimValue = "Full Trim";
    } else {
      trimValue = impellerTrim;
    }
    return trimValue;
  }

  // Get discounted price of part.
  function getDiscountedPrice(price: number | null, quantity: string, companyDiscount?: number | null): string {
    if (price === null) {
      return "N/A";
    } else if (companyDiscount === null || companyDiscount === undefined) {
      return (Number(quantity) * price).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      const totalPrice = Number(quantity) * price;
      return (totalPrice - totalPrice * (companyDiscount / 100)).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }

  return (
    <Fragment>
      <div className={styles.printContent} ref={contentToPrint}>
        <h5 className={styles.pdfTitle}>{currentUser.userName}&apos;s Shopping Cart</h5>
        <table className={`${styles.table} table mb-0 pb-0`}>
          <thead>
            <tr>
              <th>Description</th>
              <th>Part Number</th>
              <th>Quantity</th>
              <th>Weight</th>
              <th>Paint Color</th>
              <th>Impeller Trim</th>
              <th>Impeller Angle</th>
              <th>Price</th>
              <th>Discount Price</th>
              <th>Ready To Ship</th>
            </tr>
          </thead>
          <tbody>
            {props.cartItems.map((item, i) => (
              <tr key={i}>
                <td>{item.name}</td>
                <td>{item.partNumber}</td>
                <td>
                  {item.quantity}&nbsp;{item.units}
                </td>
                <td>{item.weight}&nbsp;lbs</td>
                <td>{getPaintColorName(item.paintColorId)}</td>
                <td>{getImpellerTrim(item.impellerTrim)}</td>
                <td>{item.impellerAngle === null ? "N/A" : item.impellerAngle}</td>
                <td>
                  {item.price === null
                    ? "N/A"
                    : `$${(Number(item.quantity) * item.price).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                </td>
                <td>{`$${getDiscountedPrice(item.price, item.quantity, props.companyDiscount)}`}</td>
                <td>
                  <LeadTime
                    className="justify-content-start pt-1"
                    quantity={item.quantity}
                    partNumber={item.partNumber}
                    onLeadTimeCalculated={(dateAvailable) =>
                      props.onUpdateCartItemDateAvailable(item.cartItemId, dateAvailable)
                    }
                    paintColorId={item.paintColorId}
                    isImpeller={item.impellerAngle === null || item.impellerTrim === null ? false : true}
                    impellerTrim={item.impellerTrim}
                    impellerAngle={item.impellerAngle}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="d-flex justify-content-end text-align-end pe-3 pt-2 border-bottom">
          <div className="row">
            <div className="col">
              {props.listPrice !== null && props.listPrice !== 0 ? (
                <Fragment>
                  {props.companyDiscount !== null ? (
                    <ul>
                      {props.itemCount > 1 ? (
                        <li className={styles.priceCategory}>
                          Subtotal ({props.itemCount} items):{" "}
                          <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                            $
                            {props.listPrice.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </li>
                      ) : (
                        <li className={styles.priceCategory}>
                          Subtotal ({props.itemCount} item):{" "}
                          <span className={styles.priceValue} style={{ textDecoration: "line-through" }}>
                            $
                            {props.listPrice.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </li>
                      )}
                      <li className={styles.priceCategory}>
                        {props.companyName} Discount:{" "}
                        <span className={styles.priceValue}>{props.companyDiscount}%</span>
                      </li>
                      <li className={styles.priceCategory}>
                        Total after discount:{" "}
                        <span className={styles.priceValue}>
                          $
                          {(props.listPrice - props.listPrice * (props.companyDiscount / 100)).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>
                      <li className={styles.priceCategory}>Applicable shipping and sales tax will be added</li>
                    </ul>
                  ) : (
                    <ul className={styles.priceList} data-test="price-list">
                      <li className={styles.priceCategory}>
                        Total:{" "}
                        <span data-test="price-list-number" className={styles.priceValue}>
                          $
                          {props.listPrice.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>
                      <li className={styles.priceCategory}>Applicable shipping and sales tax will be added</li>
                    </ul>
                  )}
                </Fragment>
              ) : (
                <Fragment></Fragment>
              )}
            </div>
          </div>
        </div>
      </div>
      <button type="button" data-test="print-cart-items-btn" className={`${styles.btn} w-100`} onClick={handlePrint}>
        <span className="d-none d-md-inline">Print Cart Items</span>
        <i className="d-inline d-md-none fa fa-2x fa-print" />
      </button>
    </Fragment>
  );
}

interface Props {
  cartItems: CartItem[];
  paintColors: PaintColor[];
  companyName: string | null;
  companyDiscount: number | null;
  itemCount: number;
  listPrice: number | null;
  onUpdateCartItemDateAvailable: (itemId: number, dateAvailable: string | null) => void;
}

interface CartItem {
  cartItemId: number;
  name: string;
  partId: number;
  partNumber: string;
  paintColorId: number | null;
  quantity: string;
  quantityIsValid: boolean;
  units: string;
  price: number | null;
  weight: number | null;
  impellerTrim: string | null;
  impellerTrimIsValid: boolean;
  impellerAngle: string | null;
  impellerAngleIsValid: boolean;
  dateAvailable: string | null;
  isPaintable: boolean;
}

interface PaintColor {
  paintColorId: number;
  name: string;
  hexCode: string;
}
